import { Component, computed, effect, inject, input, OnInit, Signal } from '@angular/core';

import { select, Store } from '@ngxs/store';
import { TranslocoDirective } from '@jsverse/transloco';
import { TrackButtonDirective } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';
import { SaComponent } from 'smartauction-frontend-micro-library';
import {
  BidsOffersFilters,
  dealerFilterMap,
  OfferFilters,
  QuestionFilters
} from '../../enums/inventory-management.enum';
import {
  dealerFilterConfigurationMap,
  InventoryManagementState,
  SearchResultsAggregationBucket,
  UpdateInventoryManagementDealerFilterConfiguration
} from 'remarketing-angular-library';
import { SearchFilterService } from '../../services/search-filter/search-filter.service';
import { DealerFilterService } from '../../services/dealer-filter/dealer-filter.service';

@Component({
  selector: 'app-dealer-filter',
  templateUrl: './dealer-filter.component.html',
  styleUrls: ['./dealer-filter.component.scss'],
  imports: [TranslocoDirective, TrackButtonDirective, CommonModule]
})
export class DealerFilterComponent extends SaComponent implements OnInit {
  dealerList = input<SearchResultsAggregationBucket[]>([]);
  dealerFilter = input<BidsOffersFilters | OfferFilters | QuestionFilters>(OfferFilters.NONE);
  searchBoxDisabled = input<boolean>(false);
  btnShowMore = input<string>('');

  private readonly inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);

  protected initialDealersShown = 9;

  // Select All is only visually checked when every dealer in the visible list is explicitly present in the search criteria.
  // A null criteria (meaning "all dealers" to the backend) is treated as visually unchecked.
  protected isSelectAllButtonChecked$: Signal<boolean> = computed(() => {
    const currentSearchCriteria = this.inventoryManagementSearchCriteria$();
    const computedDealerList$ = this.dealerFilterService.getComputedDealerList$();

    const dealerListSelected = currentSearchCriteria[dealerFilterConfigurationMap[dealerFilterMap[this.dealerFilter()]]];

    if (!dealerListSelected?.length) {
      return false;
    }

    return computedDealerList$().every(item => dealerListSelected.includes(item.value));
  });

  // computed signal set for better filter performance
  protected dealersSelectedSignalSet$: Signal<Set<string> | null> = computed(() => {
    const currentSearchCriteria = this.inventoryManagementSearchCriteria$();

    if (!currentSearchCriteria[dealerFilterConfigurationMap[dealerFilterMap[this.dealerFilter()]]]) {
      return null;
    }
    return new Set<string>(currentSearchCriteria[dealerFilterConfigurationMap[dealerFilterMap[this.dealerFilter()]]]);
  });

  private readonly store: Store;
  private readonly searchFilterService: SearchFilterService;
  private readonly dealerFilterService: DealerFilterService;

  constructor() {
    super();
    this.store = inject(Store);
    this.searchFilterService = inject(SearchFilterService);
    this.dealerFilterService = inject(DealerFilterService);
    effect(() => {
      this.setDefaultDealerFilters();
    });
  }

  get searchTerm() {
    return this.dealerFilterService.getSearchTerm();
  }

  get computedDealerList() {
    return this.dealerFilterService.getComputedDealerList();
  }

  get isSearchBoxDisabled() {
    if (this.searchBoxDisabled()) {
      return true;
    } else if (this.dealerFilter() === OfferFilters.DEALERS_WITH_COUNTER_OFFERS) {
      return this.dealerList()?.length <= 10;
    }
    return false;
  }

  setDefaultDealerFilters() {
    this.dealerFilterService.setDealerList(this.dealerList());
    this.dealerFilterService.setSearchTerm('');
  }

  search(e: any) {
    const searchTerm = e.target.value.replace(/[^ -~]+/g, '').toLowerCase();
    this.dealerFilterService.setSearchTerm(searchTerm);
  }

  dealerById(index: number, dealer: SearchResultsAggregationBucket) {
    return dealer.value;
  }

    toggleSelectAll() {
    const filteredDealerUuids = this.computedDealerList.map(item => item.value);
    const allDealerUuids = this.dealerList().map(item => item.value);

    const filterKey = dealerFilterConfigurationMap[dealerFilterMap[this.dealerFilter()]];
    const currentSelection: string[] | null = this.store.selectSnapshot(InventoryManagementState.searchCriteria)[filterKey];

    // Get currently selected dealers (if null/empty, none are visually selected)
    const currentlySelectedUuids: string[] = currentSelection ?? [];

    // Check if all filtered dealers are currently selected
    const allFilteredSelected = filteredDealerUuids.length > 0
      && filteredDealerUuids.every(uuid => currentlySelectedUuids.includes(uuid));

    let newSelection: string[];
    if (allFilteredSelected) {
      // Deselect all filtered dealers, keep others
      newSelection = currentlySelectedUuids.filter(uuid => !filteredDealerUuids.includes(uuid));
    } else {
      // Select all filtered dealers, keep others
      newSelection = [...new Set([...currentlySelectedUuids, ...filteredDealerUuids])];
    }

    // Update isSelectAllChecked to reflect whether all dealers are now selected
    const dealerFilterConfiguration = { ...this.store.selectSnapshot(InventoryManagementState.dealerFilterConfiguration)[filterKey] };
    dealerFilterConfiguration.isSelectAllChecked = newSelection.length > 0 && allDealerUuids.every(uuid => newSelection.includes(uuid));
    this.store.dispatch(new UpdateInventoryManagementDealerFilterConfiguration(filterKey, dealerFilterConfiguration));

    // If no dealers remain selected, reset to null (no explicit filter)
    if (newSelection.length === 0) {
      this.searchFilterService.setDealerList(dealerFilterMap[this.dealerFilter()], null);
    } else {
      this.searchFilterService.setDealerList(dealerFilterMap[this.dealerFilter()], newSelection);
    }
  }

  ngOnInit(): void {
    this.setInitialDealersShown();
  }

  setInitialDealersShown() {
    if (this.dealerFilter() === OfferFilters.DEALERS_WITH_COUNTER_OFFERS) {
      this.initialDealersShown = 10;
    }
  }

  isSelectAllChecked() {
    const currentSelectAllStatus = this.store.selectSnapshot(InventoryManagementState.dealerFilterConfiguration);

    return currentSelectAllStatus[dealerFilterConfigurationMap[dealerFilterMap[this.dealerFilter()]]].isSelectAllChecked;
  }

  toggleShowAll() {
    this.searchFilterService.toggleShowAll(this.dealerFilter());
  }

  isShowAll() {
    const currentShowAllStatus = this.store.selectSnapshot(InventoryManagementState.dealerFilterConfiguration);

    return currentShowAllStatus[dealerFilterConfigurationMap[dealerFilterMap[this.dealerFilter()]]].isShowAll;
  }

  toggleDealer(dealerUuid: string) {
    this.searchFilterService.toggleDealer(this.dealerFilter(), dealerUuid);
  }

  isDealerSelected(dealerUuid: string): boolean {
    if (!this.dealersSelectedSignalSet$()) {
      return false;
    }

    return this.dealersSelectedSignalSet$().has(dealerUuid);
  }
}
